import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ModuleCatalogEntryResponse } from '@tabita/contracts';
import { formatMoney } from '@tabita/i18n';
import { useI18n } from '@/i18n/i18n-provider';
import { moduleCatalogApi } from '@/services/module-catalog.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, Field, Switch } from '@/components/ui';

const QUERY_KEY = ['platform', 'modules'];

function errorMessage(error: unknown, t: ReturnType<typeof useI18n>['t']): string {
  return error instanceof ApiError
    ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
    : t('errors.INTERNAL_ERROR');
}

function priceLabel(
  entry: ModuleCatalogEntryResponse,
  locale: string,
  t: ReturnType<typeof useI18n>['t'],
): string {
  if (entry.isFree) return t('platform.modules.free');
  return t('platform.modules.priceMonthly', {
    values: { amount: formatMoney(entry.priceMonthlyCents ?? 0, 'USD', locale) },
  });
}

function ModuleCard({
  entry,
  titleByKey,
}: {
  entry: ModuleCatalogEntryResponse;
  titleByKey: ReadonlyMap<string, string>;
}): ReactNode {
  const { t, locale } = useI18n();
  const queryClient = useQueryClient();

  const [expanded, setExpanded] = useState(false);
  const [title, setTitle] = useState<string | null>(null);
  const [description, setDescription] = useState<string | null>(null);
  const [isFree, setIsFree] = useState<boolean | null>(null);
  const [priceInput, setPriceInput] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const effectiveTitle = title ?? entry.title;
  const effectiveDescription = description ?? entry.description;
  const effectiveIsFree = isFree ?? entry.isFree;
  const effectivePrice =
    priceInput ?? (entry.priceMonthlyCents !== null ? String(entry.priceMonthlyCents / 100) : '');

  const save = useMutation({
    mutationFn: () =>
      moduleCatalogApi.update(entry.key, {
        title: effectiveTitle,
        description: effectiveDescription,
        isFree: effectiveIsFree,
        priceMonthlyCents: effectiveIsFree
          ? undefined
          : Math.round(Number.parseFloat(effectivePrice || '0') * 100),
      }),
    onSuccess: async () => {
      setSaveError(null);
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (error: unknown) => setSaveError(errorMessage(error, t)),
  });

  return (
    <div className="settings-card oauth-provider-card">
      <div className="oauth-provider-header">
        <button
          type="button"
          className="oauth-provider-summary"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
        >
          <span className="oauth-provider-name">{entry.title}</span>
          <span className="badge">{t(`platform.modules.category.${entry.category}`)}</span>
          <span className="badge" data-status={entry.configured ? 'ACTIVE' : undefined}>
            {priceLabel(entry, locale, t)}
          </span>
          {entry.isCore && <span className="badge">{t('platform.modules.core')}</span>}
        </button>
      </div>

      {entry.dependencies.length > 0 && (
        <p className="settings-status-hint">
          {t('platform.modules.requires', {
            values: {
              modules: entry.dependencies.map((key) => titleByKey.get(key) ?? key).join(', '),
            },
          })}
        </p>
      )}

      {!expanded && (
        <button type="button" className="settings-inline-link" onClick={() => setExpanded(true)}>
          {t('platform.modules.edit')}
        </button>
      )}

      {expanded && (
        <form
          className="oauth-provider-form"
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate();
          }}
        >
          <Field
            label={t('platform.modules.moduleTitle')}
            name={`${entry.key}-title`}
            value={effectiveTitle}
            onChange={(event) => setTitle(event.target.value)}
            required
          />

          <div className="field">
            <label className="field-label" htmlFor={`${entry.key}-description`}>
              {t('platform.modules.description')}
            </label>
            <textarea
              id={`${entry.key}-description`}
              className="field-input oauth-key-textarea"
              value={effectiveDescription}
              onChange={(event) => setDescription(event.target.value)}
            />
          </div>

          <Switch
            checked={!effectiveIsFree}
            onChange={(checked) => setIsFree(!checked)}
            label={t('platform.modules.paidToggle')}
          />

          {!effectiveIsFree && (
            <Field
              label={t('platform.modules.priceLabel')}
              name={`${entry.key}-price`}
              type="number"
              min={0}
              step="0.01"
              value={effectivePrice}
              onChange={(event) => setPriceInput(event.target.value)}
              required
            />
          )}

          {saveError && <Alert tone="danger">{saveError}</Alert>}

          <div className="oauth-provider-form-actions">
            <Button type="submit" variant="primary" loading={save.isPending}>
              {t('common.actions.save')}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setExpanded(false)}>
              {t('common.actions.cancel')}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

/**
 * Platform-wide module catalog administration.
 *
 * Not a tenant page — gated on `platform.module.*`, the same TABITA-operator
 * boundary the SMTP and sign-in provider screens already establish. Every
 * module the code declares shows up here, priced or not; pricing one is the
 * only thing an operator does on this screen — dependencies and lifecycle
 * status are facts about the implementation, shown but not editable.
 */
export function ModuleCatalogPage(): ReactNode {
  const { t } = useI18n();

  const modules = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => moduleCatalogApi.list(),
  });

  const titleByKey = new Map((modules.data ?? []).map((entry) => [entry.key, entry.title]));

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{t('platform.modules.title')}</h1>
          <p className="page-count">{t('platform.modules.subtitle')}</p>
        </div>
      </header>

      {modules.isError && <Alert tone="danger">{errorMessage(modules.error, t)}</Alert>}

      <div className="oauth-provider-list">
        {modules.data?.map((entry) => (
          <ModuleCard key={entry.key} entry={entry} titleByKey={titleByKey} />
        ))}
      </div>
    </div>
  );
}
